export const USER_STATUS_FAILURE_ACTIONS = Object.freeze({
  KEEP_SESSION: "keep-session",
  LOGOUT: "logout",
  RETRY: "retry",
});

export const USER_STATUS_MAX_ATTEMPTS = 3;

const ACCOUNT_INVALIDATION_CODES = new Set([
  "ACCOUNT_INACTIVE",
  "ADMIN_ACCOUNT_INACTIVE",
  "COMPANY_DISABLED",
  "COMPANY_INACTIVE",
]);

const normalizeErrorCode = (code) =>
  typeof code === "string" ? code.trim().toUpperCase() : "";

export const decideUserStatusFailure = ({ status, code } = {}) => {
  const normalizedStatus = Number(status);
  const normalizedCode = normalizeErrorCode(code);

  if (normalizedStatus === 401) {
    return {
      action: USER_STATUS_FAILURE_ACTIONS.LOGOUT,
      reason: normalizedCode || "SESSION_INVALID",
    };
  }

  if (
    normalizedStatus === 403 &&
    ACCOUNT_INVALIDATION_CODES.has(normalizedCode)
  ) {
    return {
      action: USER_STATUS_FAILURE_ACTIONS.LOGOUT,
      reason: normalizedCode,
    };
  }

  if (normalizedStatus === 404 && normalizedCode === "USER_NOT_FOUND") {
    return {
      action: USER_STATUS_FAILURE_ACTIONS.LOGOUT,
      reason: normalizedCode,
    };
  }

  if (
    normalizedStatus === 408 ||
    normalizedStatus === 429 ||
    normalizedStatus >= 500
  ) {
    return {
      action: USER_STATUS_FAILURE_ACTIONS.RETRY,
      reason: normalizedCode || "SERVICE_TEMPORARILY_UNAVAILABLE",
    };
  }

  return {
    action: USER_STATUS_FAILURE_ACTIONS.KEEP_SESSION,
    reason: normalizedCode || "STATUS_CHECK_REJECTED",
  };
};

const FALLBACK_RETRY_DELAYS_MS = [1000, 3000];
const MIN_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 30000;

const clampRetryDelay = (delayMs) =>
  Math.min(MAX_RETRY_DELAY_MS, Math.max(MIN_RETRY_DELAY_MS, delayMs));

export const getUserStatusRetryDelayMs = (attempt, retryAfterHeader) => {
  const retryAfterValue = String(retryAfterHeader || "").trim();

  if (retryAfterValue) {
    const retryAfterSeconds = Number(retryAfterValue);
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
      return clampRetryDelay(retryAfterSeconds * 1000);
    }

    const retryAt = Date.parse(retryAfterValue);
    if (Number.isFinite(retryAt)) {
      return clampRetryDelay(retryAt - Date.now());
    }
  }

  const delayIndex = Math.min(
    Math.max(Number(attempt) - 1, 0),
    FALLBACK_RETRY_DELAYS_MS.length - 1,
  );
  return FALLBACK_RETRY_DELAYS_MS[delayIndex];
};
